
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface LiveStats {
  totalDocuments: number;
  totalConversations: number;
  activeUsers: number;
  totalOrganizations: number;
  documentTypes: number;
  averageGrowthRate: number;
}

export const useLiveStats = () => {
  const [stats, setStats] = useState<LiveStats>({
    totalDocuments: 0,
    totalConversations: 0,
    activeUsers: 0,
    totalOrganizations: 0,
    documentTypes: 0,
    averageGrowthRate: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchLiveStats = async () => {
    try {
      console.log('Fetching live stats...');
      
      // Get total documents
      const { count: documentsCount, error: docsError } = await supabase
        .from('documents')
        .select('*', { count: 'exact', head: true });

      if (docsError) {
        console.error('Error fetching documents:', docsError);
      } else {
        console.log('Documents count:', documentsCount);
      }

      // Get total chat conversations
      const { count: conversationsCount, error: convError } = await supabase
        .from('chat_conversations')
        .select('*', { count: 'exact', head: true });

      if (convError) {
        console.error('Error fetching conversations:', convError);
      } else {
        console.log('Conversations count:', conversationsCount);
      }

      // Get active users (users with subscriptions + active trials)
      const { count: subscriptionsCount, error: subError } = await supabase
        .from('user_subscriptions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active');

      if (subError) {
        console.error('Error fetching subscriptions:', subError);
      } else {
        console.log('Active subscriptions:', subscriptionsCount);
      }

      const { count: activeTrialsCount, error: trialError } = await supabase
        .from('free_trials')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active');

      if (trialError) {
        console.error('Error fetching trials:', trialError);
      } else {
        console.log('Active trials:', activeTrialsCount);
      }

      // Get total organizations
      const { count: organizationsCount, error: orgError } = await supabase
        .from('organizations')
        .select('*', { count: 'exact', head: true });

      if (orgError) {
        console.error('Error fetching organizations:', orgError);
      } else {
        console.log('Organizations count:', organizationsCount);
      }

      // Get unique document types
      const { data: documentTypesData, error: typesError } = await supabase
        .from('documents')
        .select('type')
        .not('type', 'is', null);

      if (typesError) {
        console.error('Error fetching document types:', typesError);
      } else {
        console.log('Document types data:', documentTypesData);
      }

      const uniqueTypes = new Set(documentTypesData?.map(doc => doc.type) || []);
      console.log('Unique document types:', uniqueTypes.size);

      // Calculate weekly growth rate
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);

      const { count: recentDocuments, error: growthError } = await supabase
        .from('documents')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', weekAgo.toISOString());

      if (growthError) {
        console.error('Error fetching recent documents:', growthError);
      } else {
        console.log('Recent documents (last week):', recentDocuments);
      }

      const growthRate = documentsCount && documentsCount > 0 
        ? Math.round(((recentDocuments || 0) / documentsCount) * 100)
        : 15; // Default growth rate if no data

      const newStats = {
        totalDocuments: documentsCount || 0,
        totalConversations: conversationsCount || 0,
        activeUsers: (subscriptionsCount || 0) + (activeTrialsCount || 0),
        totalOrganizations: organizationsCount || 0,
        documentTypes: uniqueTypes.size || 12, // Default to 12 if no data
        averageGrowthRate: growthRate,
      };

      console.log('Final stats:', newStats);
      setStats(newStats);

    } catch (error) {
      console.error('Error fetching live stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLiveStats();

    // Set up real-time subscriptions for automatic updates
    const documentsChannel = supabase
      .channel('documents-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'documents' }, 
        () => {
          console.log('Documents changed, refetching stats...');
          fetchLiveStats();
        }
      )
      .subscribe();

    const conversationsChannel = supabase
      .channel('conversations-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'chat_conversations' }, 
        () => {
          console.log('Conversations changed, refetching stats...');
          fetchLiveStats();
        }
      )
      .subscribe();

    const subscriptionsChannel = supabase
      .channel('subscriptions-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'user_subscriptions' }, 
        () => {
          console.log('Subscriptions changed, refetching stats...');
          fetchLiveStats();
        }
      )
      .subscribe();

    const trialsChannel = supabase
      .channel('trials-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'free_trials' }, 
        () => {
          console.log('Trials changed, refetching stats...');
          fetchLiveStats();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(documentsChannel);
      supabase.removeChannel(conversationsChannel);
      supabase.removeChannel(subscriptionsChannel);
      supabase.removeChannel(trialsChannel);
    };
  }, []);

  return { stats, isLoading, refetch: fetchLiveStats };
};
